/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent, BuilderNavigationService, BuilderUserContext, CommonActions, FormActionPipelines, FormFlowState, jobSpecCreateDialogRoute, } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermission,
  getAelEventsArray,
  SpecTemplateClauses,
} from '@gh/builder-shared-data';
import { textMessageDialogRoute, TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow, AppRouter, constantNoop, FormStateSnapshot, getFormStateSnapshotValue, getRouteInitData, getRouteParam, getRouteParentTitle, } from '@gh/common-shared';
import { ID, PrintEntryPoint } from '@gh/common-shared-data';
import { MessageSeverity } from '@gh/core-messages';
import { TranslationService, TranslationTupleOrId } from '@gh/core-mls';
import {
  DialogService, NotificationService, TextMessageDialogConfig, yesAnswer, yesNoConfirmation, } from '@gh/core-ui';
import { createDefaultTreeOptions, goToNodeByPath, isTreeNode } from '@gh/core-ui-ext/tree';
import { createLink, getLocalValue, setLocalValue } from '@gh/core-util';
import { map$, filter$, finalize$, first$, startWith$, switchMap$ } from '@gh/rx/operators';
import { ITreeState, TREE_ACTIONS, TreeComponent, TreeNode } from '@ali-hm/angular-tree-component';
import { assign, filter, first, isEmpty, isNil } from 'lodash';
import { JobSpecBusinessService } from '../../resources/job-spec-business.service';
import {
  getPreviewTypeLabel,
  JobSpecDetails,
  JobSpecInfo,
  JobSpecParagraphDetails,
  JobSpecParagraphInfo,
  JobSpecQuoteChange,
  JobSpecSectionDetails,
  JobSpecSectionInfo,
  JobSpecSelectionLevel,
  jobSpecSelectionToTreePath,
} from '../../resources/job.model';
import { JobSpecDetailsFormState } from './job-spec-details-form.service';
import { Observable, Subscription } from 'rxjs';

const JOB_SPEC_AUTO_SAVE_LOCAL_KEY = 'jobSpec:autoSave';

@Component({
  standalone: false,
  selector: 'gh-job-spec',
  templateUrl: 'job-spec-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JobSpecDetailsFormState],
})
export class JobSpecDetailsComponent extends AbstractFormComponent<JobSpecDetails> implements OnInit, OnDestroy {

  treeOptions = createDefaultTreeOptions({
    idField: 'id',
    childrenField: 'children',
    // allow to drag and drop only quote changes
    allowDrag: (node) => node && node.data && node.data.isDraggable,
    // we have to distinguish whether we drop from grid or tree component
    allowDrop: (node, { parent }) =>
      (parent.level === 1 || parent.level === 2
        && getFormStateSnapshotValue<any>(parent.data.info).clausesAre === SpecTemplateClauses.Allowed)
      && !parent.data.isOqc
      && (isTreeNode(node) && !!node.data ? node.data.isDraggable : node.isDraggable),
    nodeClass: (node) => node.level === 1 ? 'node-expandable' : '',
    // We disable selection on click and handle this behavior yourself.
    // Selection on click is not suitable when click on buttons.
    actionMapping: {
      mouse: {
        click: TREE_ACTIONS.ACTIVATE,
        drop: (tree: any, node: any, $event: any, { from, to }: any) => {
          // allow to drop tree node
          if (isTreeNode(from)) {
            if (to.parent.level === 1) {
              TREE_ACTIONS.MOVE_NODE.apply(void 0, [tree, node, $event, { from, to }]);
            } else {
              this.moveQuoteChangeFromParagraphToSection(from.parent.data, from.data, to.parent.data);
            }
          } else {
            if (to.parent.level === 1) {
              // allow to drop quote change as section from grid component
              this.moveQuoteChangeIntoParagraph(to.parent.data, to.index, from);
            } else {
              // allow to drop quote change as clause from grid component
              this.moveQuoteChangeIntoSection(to.parent.data, from);
            }
          }
        },
      },
    },
  });

  jobId: ID;
  forDpp: boolean;
  autoSaveIntervals = ['5m', '10m', '15m', '30m', '40m', '60m'];
  currentAutoSaveInterval = getLocalValue(JOB_SPEC_AUTO_SAVE_LOCAL_KEY, '30m');
  autoSaveActivityTick = 0;
  treeState: ITreeState = {};
  isUndoDisabled$: Observable<boolean>;
  isRedoDisabled$: Observable<boolean>;
  private autoSaveActivitySubscription?: Subscription;

  @ViewChild(TreeComponent, { static: false }) tree: TreeComponent;

  constructor(private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private changeDetectorRef: ChangeDetectorRef,
    public form: JobSpecDetailsFormState,
    private business: JobSpecBusinessService,
    private dialogService: DialogService,
    private notifications: NotificationService,
    private route: ActivatedRoute,
    private router: AppRouter,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private navigationService: BuilderNavigationService,
    private userContext: BuilderUserContext) {
    super();
  }

  get editable(): boolean {
    return this.form.editable;
  }

  get completeFlow(): ActionFlow<FormFlowState<JobSpecDetails>> {
    return this.pipelines.complete({
      successMessage: 'msg_job_spec_successfully',
      execute: this.commonActions.execute((state) => this.business.save(this.jobId, state.value)),
      finish: (state: FormFlowState<JobSpecDetails>) => {
        const { value } = state;
        this.form.resetDirty();

        if (this.form.createMode) {
          const jobId = getRouteParam(this.route, 'jobId');
          this.router.switchTo([`/job-admin/jobs`, jobId, 'spec'], { omitQueryParams: ['specTemplateId'] });
          this.form.initHistory();
        } else {
          this.form.merge(value);
        }
        this.isUndoDisabled$ = this.form.changes.pipe(
          startWith$(void 0),
          map$(() => this.form.undoDisabled)
        );
        this.isRedoDisabled$ = this.form.changes.pipe(
          startWith$(void 0),
          map$(() => this.form.redoDisabled)
        );
      },
    });
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_jobs';
  }

  get masterInfo(): FormStateSnapshot<JobSpecInfo> {
    return this.form.value.masterInfo;
  }

  get jobNumber(): string {
    return getFormStateSnapshotValue(this.masterInfo).jobNumber;
  }

  get currentParagraph(): Maybe<JobSpecParagraphDetails> {
    return this.form.selection.paragraph;
  }

  get currentSection(): Maybe<JobSpecSectionDetails> {
    return this.form.selection.section;
  }

  get paragraphs(): JobSpecParagraphDetails[] {
    return this.form.value ? this.form.value.paragraphs : [];
  }

  get isQuoteStructure(): boolean {
    return !isNil(this.currentParagraph) && this.currentParagraph.isOqc;
  }

  get isParagraphSelected(): boolean {
    return this.form.selection.level === JobSpecSelectionLevel.Paragraph;
  }

  get isSectionSelected(): boolean {
    return this.form.selection.level === JobSpecSelectionLevel.Section;
  }

  get paragraphSequence(): number {
    return isNil(this.currentParagraph) ? 0 : this.form.value.paragraphs.indexOf(this.currentParagraph);
  }

  get sectionSequence(): number {
    return isNil(this.currentParagraph) || isNil(this.currentSection) ?
      0 : this.currentParagraph.children.indexOf(this.currentSection);
  }

  get quoteChanges(): JobSpecQuoteChange[] {
    return this.form.quoteChanges;
  }

  get isCurrentParagraphIncluded(): boolean {
    const { currentParagraph } = this;
    return currentParagraph ? getFormStateSnapshotValue(currentParagraph.info).isParagraphIncluded : false;
  }

  get isUndoRedoVisible(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT);
  }

  ngOnInit(): void {
    const jobId = getRouteParam(this.route, 'jobId');
    const { jobSpec, forDpp } = getRouteInitData(this.route);

    assign(this, { jobId, forDpp });

    this.form.selectionChanges.pipe(
      map$(jobSpecSelectionToTreePath),
      filter$(Boolean),
      // wait while all processing of new/removed nodes is completed by tree component
      switchMap$((path) => this.ngZone.onMicrotaskEmpty.pipe(first$(), map$(() => path))))
      .subscribe((path: any) => {
        this.treeState = goToNodeByPath(this.treeState, path);
        this.changeDetectorRef.markForCheck();
      });
    this.form.build(jobSpec);

    this.autoSaveActivitySubscription = this.form.changes.subscribe(() => {
      if (this.form.dirty) {
        this.autoSaveActivityTick++;
      }
      this.changeDetectorRef.markForCheck();
    });
    // Initialize undo/redo observables
    this.isUndoDisabled$ = this.form.changes.pipe(
      startWith$(void 0),
      map$(() => this.form.undoDisabled)
    );
    this.isRedoDisabled$ = this.form.changes.pipe(
      startWith$(void 0),
      map$(() => this.form.redoDisabled)
    );
  }

  ngOnDestroy(): void {
    this.autoSaveActivitySubscription?.unsubscribe();
  }

  getParagraphTitle(paragraph: JobSpecParagraphDetails): string {
    const paragraphInfo = getFormStateSnapshotValue(paragraph.info);

    return paragraph.isOqc ? `${paragraphInfo.name} (${paragraph.quoteChanges.length})` : paragraphInfo.name;
  }

  getSectionTitle(section: JobSpecSectionDetails): string {
    return getFormStateSnapshotValue(section.info).name;
  }

  isParagraphIncluded(paragraph: JobSpecParagraphDetails | null | undefined): boolean {
    return paragraph ? getFormStateSnapshotValue(paragraph.info).isParagraphIncluded : false;
  }

  isSectionIncluded(section: JobSpecSectionDetails | null | undefined): boolean {
    return section ? getFormStateSnapshotValue(section.info).isSectionIncluded : false;
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      eventTypeIds: getAelEventsArray(AelEvents.JobSpec),
      label: 'lbl_job_specification',
    });
  }

  onAutoSave(event: TimerEvent): void {
    if (!this.form.dirty) {
      return;
    }
    const savingNotification = this.notifications.show({
      messageId: 'lbl_saving',
      severity: MessageSeverity.Success,
      hideDelay: 30000,
    });
    const flow = this.completeFlow;
    const save$ = flow.run({ trigger: 'process', form: this.form, value: this.form.value }).toFinalState().pipe(
      finalize$(() => {
        savingNotification.close();
      })
    );
    event.waitFor(save$);
  }

  onChangeAutoSaveInterval(interval: string): void {
    setLocalValue(JOB_SPEC_AUTO_SAVE_LOCAL_KEY, interval);
  }

  onProduce(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
      return;
    }

    const oqc = first(filter(this.paragraphs, (paragraph) => paragraph.isOqc));
    if (isEmpty(oqc) || isEmpty(oqc!.quoteChanges)) {
      this.printFlow(PrintEntryPoint.Produce);
      return;
    }

    this.dialogService.confirmation(yesNoConfirmation('msg_spec_other_qc_produce')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.printFlow(PrintEntryPoint.Produce);
      });
  }

  onMoveNode(): void {
    this.form.moveNodes();
  }

  onCurrentParagraphDataChange(paragraphData: FormStateSnapshot<JobSpecParagraphInfo>): void {
    const { currentParagraph } = this;

    if (currentParagraph) {
      this.form.updateParagraphData(currentParagraph, paragraphData);
    }
  }

  onCurrentSectionDataChange(sectionData: FormStateSnapshot<JobSpecSectionInfo>): void {
    const { currentSection } = this;

    if (currentSection) {
      this.form.updateSectionData(currentSection, sectionData);
    }
  }

  onMoveSectionQuoteChangeBack(paragraph: JobSpecParagraphDetails, section: JobSpecSectionDetails): void {
    if (!paragraph || !section) {
      return;
    }
    this.form.backSectionQuoteChange(paragraph, section);
    this.updateTree();
  }

  onMoveClauseQuoteChangeBack(section: JobSpecSectionDetails, quoteChange: JobSpecQuoteChange): void {
    this.form.backClauseQuoteChange(section, quoteChange);
  }

  onSelectNode(node: TreeNode): void {
    switch (node.level) {
      case JobSpecSelectionLevel.Paragraph:
        this.form.selectParagraph(node.data, { recordNavigationHistory: true });
        break;
      case JobSpecSelectionLevel.Section:
        this.form.selectSection(node.parent.data, node.data, { recordNavigationHistory: true });
        break;
      default:
        break;
    }
  }

  onPreview(): void {
    this.printFlow(PrintEntryPoint.Preview);
  }

  onReplace(): void {
    const { route } = this;
    const jobId = getRouteParam(route, 'jobId');
    this.dialogService.confirmation(yesNoConfirmation('msg_job_spec_replace')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.dialogService.openWithInput(jobSpecCreateDialogRoute, {
        isFromCreateBtn: false,
      }).afterClosed()),
      filter$(Boolean),
      switchMap$((jst: any) => this.business.get(jobId, jst.jobSpecTemplate.id, this.forDpp)))
      .subscribe((details) => {
        this.form.build(details);
        this.form.setDirty();
      });
  }

  onContactMenuChange(): void {
    this.form.markContactMenuChange();
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<JobSpecInfo>): void {
    this.form.updateMasterInfo(masterInfo);
  }

  onQuoteChangesChange(quoteChanges: JobSpecQuoteChange[]): void {
    this.form.updateQuoteChanges(quoteChanges);
  }

  undo(): void {
    if (!this.isUndoRedoVisible) {
      return;
    }
    this.form.undo();
    requestAnimationFrame(() => {
      this.changeDetectorRef.markForCheck();
      this.safeUpdateTree();
    });
  }

  redo(): void {
    if (!this.isUndoRedoVisible) {
      return;
    }
    this.form.redo();
    requestAnimationFrame(() => {
      this.changeDetectorRef.markForCheck();
      this.safeUpdateTree();
    });
  }

  private moveQuoteChangeIntoParagraph(paragraph: JobSpecParagraphDetails,
    index: number,
    qc: JobSpecQuoteChange): void {
    this.form.moveQuoteChangeIntoParagraph(paragraph, index, qc);
    this.updateTree();
  }

  private moveQuoteChangeFromParagraphToSection(sourceParagraph: JobSpecParagraphDetails,
    sourceSection: JobSpecSectionDetails,
    targetSection: JobSpecSectionDetails): void {
    this.form.moveQuoteChangeFromParagraphToSection(sourceParagraph, sourceSection, targetSection);
    this.updateTree();
  }

  private moveQuoteChangeIntoSection(section: JobSpecSectionDetails,
    qc: JobSpecQuoteChange): void {
    this.form.moveQuoteChangeIntoSection(section, qc);
    this.updateTree();
  }

  private updateTree(): void {
    requestAnimationFrame(() => {
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
      this.safeUpdateTree();
    });
  }

  private safeUpdateTree(): void {
    if (!this.tree || !this.tree.treeModel) {
      return;
    }
    this.ngZone.run(() => {
      setTimeout(() => {
        try {
          const nodes = this.tree?.treeModel?.nodes;
          if (nodes && Array.isArray(nodes) && nodes.length > 0) {
            this.tree?.treeModel?.update();
          }
          this.changeDetectorRef.markForCheck();
        } catch (error) {
          this.changeDetectorRef.markForCheck();
        }
      }, 0);
    });
  }

  private printFlow(entryPoint: PrintEntryPoint): void {
    const flow = this.pipelines.complete({
      noSuccessNotification: true,
      // do not validate form for preview flow
      validateForm: entryPoint === PrintEntryPoint.Preview ? constantNoop() : void 0,
      execute: this.commonActions.execute((state) =>
        this.business.produce(this.jobId, this.form.value, entryPoint)),
      finish: (state: FormFlowState<JobSpecDetails>) => {
        const { value } = state;

        if (entryPoint === PrintEntryPoint.Produce) {
          this.form.build(value);
          this.form.resetDirty();
        }

        const masterInfo = getFormStateSnapshotValue(value.masterInfo);

        if (masterInfo.previewLink) {
          const link = createLink(masterInfo.previewLink, masterInfo.fileName);

          this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
            title: this.translationService.get(getPreviewTypeLabel(entryPoint)),
            html: `${this.translationService.get('lbl_view_the_document_by_the_link')}:\n${link}`,
            buttons: [
              { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
              { title: this.translationService.get('lbl_close'), value: 'close' },
            ],
          });
        }
      },
    });

    flow.run({
      form: this.form,
      trigger: 'process',
      value: this.form.value,
    });
  }
}
